import { movies, type Movie, type MovieType } from "@/mocks/movies";

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((item) => item.id === id);
}

export function getMoviesByType(type: MovieType): Movie[] {
  return movies.filter((item) => item.type === type);
}

export function isValidType(value: string | undefined): value is MovieType {
  return value === "movie" || value === "series" || value === "variety";
}

export interface FilterParams {
  region?: string;
  year?: string;
  genre?: string;
  sort?: string;
}

export function filterMovies(list: Movie[], params: FilterParams): Movie[] {
  const { region, year, genre, sort } = params;
  let result = [...list];

  if (region && region !== "全部") {
    result = result.filter((item) => item.region === region);
  }
  if (year && year !== "全部") {
    if (year === "更早") {
      result = result.filter((item) => item.year < 2020);
    } else {
      result = result.filter((item) => String(item.year) === year);
    }
  }
  if (genre && genre !== "全部") {
    result = result.filter((item) => item.genres.includes(genre));
  }

  switch (sort) {
    case "popular":
      result.sort((a, b) => b.views - a.views);
      break;
    case "rating":
      result.sort((a, b) => b.rating - a.rating);
      break;
    case "year":
      result.sort((a, b) => b.year - a.year);
      break;
    case "latest":
    default:
      result.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      break;
  }
  return result;
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies.filter((item) => {
    const fields = [
      item.title,
      ...item.genres,
      item.region,
      item.director,
      item.cast,
    ];
    return fields.some((field) => field.toLowerCase().includes(q));
  });
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, limit);
}

export function getPopularMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getRelatedMovies(movie: Movie, limit = 6): Movie[] {
  const sameType = movies.filter(
    (item) => item.id !== movie.id && item.type === movie.type
  );
  const scored = sameType
    .map((item) => {
      const genreScore = item.genres.filter((g) => movie.genres.includes(g)).length;
      const regionScore = item.region === movie.region ? 1 : 0;
      return { item, score: genreScore * 2 + regionScore };
    })
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views)
    .map((entry) => entry.item);
  return scored.slice(0, limit);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万次播放`;
  }
  return `${views}次播放`;
}

export { movies };